import { INode } from 'shared/types/tree'
import { NodeIdMap } from '../../version_mapping/utility'
import { NodeType } from 'shared/types/ingestion'
import { getActivityNodes } from './hooks'
import {
  getQuestionType,
  stripTreeJsonForAppend,
} from '../../utility/TreeHelpers'
import { DeleteNodeUpdateData, UpdateNodeData } from '../nodeUpdates/types'
import { idsForNode, UpdateMethod, UpdateType } from '../nodeUpdates/utility'

export function getLessonPlan(activity: INode, idMap: NodeIdMap) {
  return getAncestorOfType(NodeType.LessonPlan, activity, idMap)
}

export function getUnit(activity: INode, idMap: NodeIdMap) {
  return getAncestorOfType(NodeType.Unit, activity, idMap)
}

export function getAncestorForCondition(
  node: INode,
  condition: (n: INode) => boolean,
  idMap: NodeIdMap,
) {
  let currentNode: INode | undefined = node
  while (currentNode && !condition(currentNode)) {
    if (currentNode.parent_id) {
      currentNode = idMap[currentNode.parent_id]?.node
    } else {
      currentNode = undefined
    }
  }
  return currentNode
}

export function getAncestorOfType(
  ancestorType: NodeType,
  descendantNode: INode,
  idMap: NodeIdMap,
) {
  return getAncestorForCondition(
    descendantNode,
    ({ type }) => ancestorType === type,
    idMap,
  )
}

function getCleanTitle(title: string) {
  const toRemove = [
    '’',
    "'",
    '-',
    'notice and wonder',
    'which three go together',
    'card sort',
  ]
  return toRemove
    .reduce(
      (acc, char) => {
        return acc.replaceAll(char, '')
      },
      title.toLowerCase().replace(/\\\((.*?)\)\\/g, '$1'),
    )
    .replace(/\s\s+/g, ' ')
    .split(':')
    .pop()!
    .trim()
}

export function doMapByTitleMatch({
  sourceTree,
  destTree,
}: {
  sourceTree: INode
  destTree: INode
}) {
  const destActivityNodes = getActivityNodes(destTree, false)
  const sourceActivityNodes = getActivityNodes(sourceTree, true).reduce<
    Record<string, INode[]>
  >((acc, node) => {
    const cleanTitle = getCleanTitle(node.title)
    if (!(cleanTitle in acc)) {
      acc[cleanTitle] = []
    }
    acc[cleanTitle].push(node)
    return acc
  }, {})

  return destActivityNodes.reduce<Record<string, INode[]>>((acc, node) => {
    return {
      ...acc,
      [node.id]: sourceActivityNodes[getCleanTitle(node.title)] ?? [],
    }
  }, {})
}

export function hasQuestions(activity: INode) {
  return !!getQuestionNodes(activity).length
}

export function getDoQuestionsMatch(activity1: INode, activity2: INode) {
  const getDescString = (node: INode) => {
    const questions =
      node.type === NodeType.Question
        ? [node]
        : node.nodes.filter(({ type }) => type === NodeType.Question)
    const typeString = questions.map((q) => getQuestionType(q)).join(',')
    const descString = questions.map(({ description }) => description).join(',')
    return `${typeString}:${descString}`
  }

  const descriptions1 = getQuestionNodes(activity1).map(getDescString).join('.')
  const descriptions2 = getQuestionNodes(activity2).map(getDescString).join('.')
  return descriptions1 === descriptions2
}

export function getVerifiedQuestionCounts(
  destinationNodeId: string,
  destIdMap: NodeIdMap | undefined,
  nodeMapping: Record<string, INode[]>,
): boolean | undefined {
  if (!destIdMap || !(destinationNodeId in destIdMap)) return
  const { node: destNode } = destIdMap[destinationNodeId]
  const sourceNode = nodeMapping[destinationNodeId][0]
  return getDoQuestionsMatch(destNode, sourceNode)
}

export function isMultiPartQuestion(node: INode) {
  return (
    node.type === NodeType.Inline &&
    node.nodes.find(({ type }) => type === NodeType.Question)
  )
}

export function isQuestionOrMultipartQuestion(node: INode) {
  return node.type === NodeType.Question || isMultiPartQuestion(node)
}

export function getQuestionNodes(node: INode) {
  return node.nodes.filter(isQuestionOrMultipartQuestion)
}

export function getQuestionSiblingNode(node: INode) {
  let foundQuestion = false
  return node.nodes.find((node) => {
    const isQuestion = isQuestionOrMultipartQuestion(node)
    if (foundQuestion && !isQuestion) return true
    if (isQuestion) {
      foundQuestion = true
    }
    return false
  })
}

export function getNodeUpdates({
  destActivityNode,
  sourceActivityNode,
}: {
  destActivityNode: INode
  sourceActivityNode: INode
}): UpdateNodeData[] {
  const questionSibling = getQuestionSiblingNode(destActivityNode)
  const sourceQuestions = getQuestionNodes(sourceActivityNode).map(
    stripTreeJsonForAppend,
  )
  const destQuestionsToDelete = getQuestionNodes(destActivityNode)
  return [
    ...destQuestionsToDelete.map<DeleteNodeUpdateData>((qNode: INode) => ({
      type: UpdateType.node,
      ids: idsForNode(qNode),
      method: UpdateMethod.delete,
    })),
    {
      type: UpdateType.node,
      ids: idsForNode(destActivityNode),
      params: {
        trees: sourceQuestions,
        next_sibling_node_id: questionSibling?.id,
      },
      method: UpdateMethod.create,
    },
  ]
}
